import { createClient } from '@supabase/supabase-js';
import { env } from '../../config/env.js';

/**
 * The browser's Supabase client.
 *
 * Authentication happens directly against Supabase Auth rather than through our
 * API. That keeps password handling, email verification, refresh-token rotation
 * and session persistence inside a maintained implementation instead of one we
 * would have to write and keep secure ourselves.
 *
 * The resulting JWT is what the API verifies, and what Row Level Security
 * evaluates. This client is therefore an *identity* client: application data is
 * read through our API, not through PostgREST from the browser, so that every
 * read and write passes the audit and validation layers.
 */
export const supabase = createClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // No auth flow in this app uses URL fragments; leaving detection on would
    // make the client parse tokens out of any URL a user happens to land on.
    detectSessionInUrl: false,
    flowType: 'pkce',
  },
});

/**
 * Current access token, refreshing it if necessary.
 *
 * Passed to the API client so no feature ever handles a token directly.
 */
export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
